import _find from 'lodash/find';
// Serialization
import { deserialize } from 'serializr';
import {
  metricOccupancyLiveSchema, metricOccupancyPeriodSchema,
  metricAccessesSchema, metricRevenuesSchema,
} from './schemas';
// Fragments
import {
  metricLiveOccupancyRateFragment,
  metricPeriodOccupancyRateFragment,
  metricPredictedOccupancyFragment,
  metricAccessesFragment,
  metricRevenuesFragment,
  metricsRevenuesComparisonFragment,
  generateInvoiceExportFragment,
} from './fragments';
// Custom
import { dateGetter } from '../../utils/DateUtils';

/**
* Serialization
*/
const deserializeMetricOccupancyLive = (data) => deserialize(metricOccupancyLiveSchema, data);
const deserializeMetricOccupancyPeriod = (data) => deserialize(metricOccupancyPeriodSchema, data);
const deserializeMetricAccesses = (data) => deserialize(metricAccessesSchema, data);
const deserializeMetricRevenues = (data) => deserialize(metricRevenuesSchema, data);

/**
* Lists
*/
const metricGranularities = {
  hourly: 'hourly',
  daily: 'daily',
  monthly: 'monthly',
};

const metricTimeReferencesList = [
  {
    key: 'dayHeader',
    labelId: 'metric.time.daySection',
    value: 'dayHeader',
    scope: 'day',
    header: true,
  },
  {
    key: 'yesterday',
    value: 'yesterday',
    labelId: 'metric.time.yesterday',
    scope: 'day',
    defaultGranularity: metricGranularities.hourly,
    pickerType: null,
  },
  {
    key: 'today',
    value: 'today',
    labelId: 'metric.time.today',
    scope: 'day',
    defaultGranularity: metricGranularities.hourly,
    pickerType: null,
  },
  {
    key: 'tomorrow',
    value: 'tomorrow',
    labelId: 'metric.time.tomorrow',
    scope: 'day',
    defaultGranularity: metricGranularities.hourly,
    pickerType: null,
  },
  {
    key: 'day',
    value: 'day',
    labelId: 'metric.time.selectSpecificDay',
    scope: 'day',
    defaultGranularity: metricGranularities.hourly,
    pickerType: 'date',
  },
  {
    key: 'weekHeader',
    labelId: 'metric.time.weekSection',
    value: 'weekHeader',
    scope: 'week',
    header: true,
  },
  {
    key: 'lastWeek',
    value: 'lastWeek',
    labelId: 'metric.time.lastWeek',
    scope: 'week',
    defaultGranularity: metricGranularities.daily,
    availableGranularities: [metricGranularities.daily, metricGranularities.hourly],
    pickerType: null,
  },
  {
    key: 'thisWeek',
    value: 'thisWeek',
    labelId: 'metric.time.thisWeek',
    scope: 'week',
    defaultGranularity: metricGranularities.daily,
    availableGranularities: [metricGranularities.daily, metricGranularities.hourly],
    pickerType: null,
  },
  {
    key: 'nextWeek',
    value: 'nextWeek',
    labelId: 'metric.time.nextWeek',
    scope: 'week',
    defaultGranularity: metricGranularities.daily,
    availableGranularities: [metricGranularities.daily, metricGranularities.hourly],
    pickerType: null,
  },
  {
    key: 'week',
    value: 'week',
    labelId: 'metric.time.selectSpecificWeek',
    scope: 'week',
    defaultGranularity: metricGranularities.daily,
    availableGranularities: [metricGranularities.daily, metricGranularities.hourly],
    pickerType: 'date',
  },
  {
    key: 'monthHeader',
    labelId: 'metric.time.monthSection',
    value: 'monthHeader',
    scope: 'month',
    header: true,
  },
  {
    key: 'lastMonth',
    value: 'lastMonth',
    labelId: 'metric.time.lastMonth',
    scope: 'month',
    defaultGranularity: metricGranularities.daily,
    availableGranularities: [metricGranularities.daily, metricGranularities.hourly],
    pickerType: null,
  },
  {
    key: 'thisMonth',
    value: 'thisMonth',
    labelId: 'metric.time.thisMonth',
    scope: 'month',
    defaultGranularity: metricGranularities.daily,
    availableGranularities: [metricGranularities.daily, metricGranularities.hourly],
    pickerType: null,
  },
  {
    key: 'nextMonth',
    value: 'nextMonth',
    labelId: 'metric.time.nextMonth',
    scope: 'month',
    defaultGranularity: metricGranularities.daily,
    availableGranularities: [metricGranularities.daily, metricGranularities.hourly],
    pickerType: null,
  },
  {
    key: 'month',
    value: 'month',
    labelId: 'metric.time.selectSpecificMonth',
    scope: 'month',
    defaultGranularity: metricGranularities.daily,
    availableGranularities: [metricGranularities.daily, metricGranularities.hourly],
    pickerType: 'yearAndMonth',
  },
  {
    key: 'yearHeader',
    labelId: 'metric.time.yearSection',
    value: 'yearHeader',
    scope: 'year',
    header: true,
  },
  {
    key: 'lastYear',
    value: 'lastYear',
    labelId: 'metric.time.lastYear',
    scope: 'year',
    defaultGranularity: metricGranularities.monthly,
    availableGranularities: [metricGranularities.monthly, metricGranularities.daily],
    pickerType: null,
  },
  {
    key: 'thisYear',
    value: 'thisYear',
    labelId: 'metric.time.thisYear',
    scope: 'year',
    defaultGranularity: metricGranularities.monthly,
    availableGranularities: [metricGranularities.monthly, metricGranularities.daily],
    pickerType: null,
  },
  {
    key: 'nextYear',
    value: 'nextYear',
    labelId: 'metric.time.nextYear',
    scope: 'year',
    defaultGranularity: metricGranularities.monthly,
    availableGranularities: [metricGranularities.monthly, metricGranularities.daily],
    pickerType: null,
  },
  {
    key: 'year',
    value: 'year',
    labelId: 'metric.time.selectSpecificYear',
    scope: 'year',
    defaultGranularity: metricGranularities.monthly,
    availableGranularities: [metricGranularities.monthly, metricGranularities.daily],
    pickerType: 'year',
  },
  {
    key: 'periodHeader',
    labelId: 'metric.time.perPeriod',
    value: 'periodHeader',
    scope: 'period',
    header: true,
  },
  {
    key: 'period',
    value: 'period',
    labelId: 'metric.time.period',
    scope: 'period',
    pickerType: null,
  },
];

const metricParkingTypeList = [
  {
    key: 'booking',
    value: 'booking',
    labelId: 'metric.parkingType.booking',
    icon: 'itemBooking',
  },
  {
    key: 'on_demand',
    value: 'on_demand',
    labelId: 'metric.parkingType.ondemand',
    icon: 'itemAccessWay',
  },
];

/**
* Utils
*/
const metricDefaultTimeReference = (timeReferencesList) => _find(timeReferencesList, ['key', 'thisMonth']);

// Return all dates for given chart configuration
const metricGetPeriodDates = ({
  timeReference, baseDate, granularity, periodBegin, periodEnd, predictedMetrics,
}) => {
  // Without period
  if (timeReference.value !== 'period') {
    // Return date for predicted chart
    if (predictedMetrics) {
      // day
      if (timeReference.scope === 'day') {
        return dateGetter.hoursArrayByDayPredicted(baseDate);
      }
      // week
      if (timeReference.scope === 'week') {
        if (granularity === metricGranularities.hourly) {
          return dateGetter.hoursArrayByWeekPredicted(baseDate);
        }
        return dateGetter.daysArrayByWeekPredicted(baseDate);
      }
      // month
      if (timeReference.scope === 'month') {
        if (granularity === metricGranularities.hourly) {
          return dateGetter.hoursArrayByMonthPredicted(baseDate);
        }
        return dateGetter.daysArrayByMonthPredicted(baseDate);
      }
      // year
      if (timeReference.scope === 'year') {
        if (granularity === metricGranularities.daily) {
          return dateGetter.daysArrayByYearPredicted(baseDate);
        }
        return dateGetter.monthsArrayByYearPredicted(baseDate);
      }
    }

    if (timeReference.scope === 'day') {
      return dateGetter.hoursArrayByDay(baseDate);
    }
    if (timeReference.scope === 'week') {
      if (granularity === metricGranularities.hourly) {
        return dateGetter.hoursArrayByWeek(baseDate);
      }
      return dateGetter.daysArrayByWeek(baseDate);
    }
    if (timeReference.scope === 'month') {
      if (granularity === metricGranularities.hourly) {
        return dateGetter.hoursArrayByMonth(baseDate);
      }
      return dateGetter.daysArrayByMonth(baseDate);
    }
    if (timeReference.scope === 'year') {
      if (granularity === metricGranularities.daily) {
        return dateGetter.daysArrayByYear(baseDate);
      }
      return dateGetter.monthsArrayByYear(baseDate);
    }
    return null;
  }
  // With period
  if (periodBegin && periodEnd) {
    if (granularity === metricGranularities.hourly) {
      return dateGetter.hoursArrayByDates(periodBegin, periodEnd);
    }
    if (granularity === metricGranularities.daily) {
      return dateGetter.daysArrayByDates(periodBegin, periodEnd);
    }
    if (predictedMetrics) {
      return dateGetter.monthsArrayByDatesPredicted(periodBegin, periodEnd, predictedMetrics);
    }
    return dateGetter.monthsArrayByDates(periodBegin, periodEnd, predictedMetrics);
  }
  return null;
};

/**
* GraphQL
*/
const queGetMetricsOccupancyRateLive = `
  query GetMetricsOccupancyRateLive($input: MetricsLiveOccupancyRateInput!) {
    metricsLiveOccupancyRate(input: $input) {
      ...metricLiveOccupancyRateFragment
    }
  }

  ${metricLiveOccupancyRateFragment}
`;

const queGetMetricsOccupancyRatePeriod = `
  query GetMetricsOccupancyRatePeriod($input: MetricsPeriodOccupancyRateInput!) {
    metricsPeriodOccupancyRate(input: $input) {
      ...metricPeriodOccupancyRateFragment
    }
  }

  ${metricPeriodOccupancyRateFragment}
`;

const queGetMetricsAccesses = `
  query GetMetricsAccesses($input: MetricsAccessesInput!) {
    metricsAccesses(input: $input) {
      ...metricAccessesFragment
    }
  }

  ${metricAccessesFragment}
`;

const queGetMetricsRevenues = `
  query GetMetricsRevenues($input: MetricsRevenuesInput!) {
    metricsRevenues(input: $input) {
      ...metricRevenuesFragment
    }
  }

  ${metricRevenuesFragment}
`;

const queGetMetricsRevenuesComparison = `
  query GetMetricsRevenuesComparison($input: MetricsRevenuesComparisonInput!) {
    metricsRevenuesComparison(input: $input) {
      ...metricsRevenuesComparisonFragment
    }
  }

  ${metricsRevenuesComparisonFragment}
`;

const queGetMetricsRevenuesReport = `
  mutation GenerateInvoiceExport($input: GenerateInvoiceExportInput!) {
    generateInvoiceExport(input: $input) {
      ...generateInvoiceExportFragment
    }
  }

  ${generateInvoiceExportFragment}
`;

const queGetMetricsOccupancyRatePredicted = `
  query GetMetricsOccupancyRatePredicted($input: MetricsPredictedOccupancyInput!) {
    metricsPredictedOccupancy(input: $input) {
      ...metricPredictedOccupancyFragment
    }
  }

  ${metricPredictedOccupancyFragment}
`;

const queGetActivityStatisticsOfTheDay = `
  query GetActivityStatisticsOfTheDay($input: GetActivityStatisticsOfTheDayInput!) {
    getActivityStatisticsOfTheDay(input: $input) {
      entries,
      exits,
      bookingsBegining,
      bookingsEnding,
      onDemandsEnding
    }
  }
`;

/**
* Model
*/
const metrics = {
  // Name and icon
  name: 'metrics',
  icon: 'metrics',

  // Serialization
  deserializeOccupancyLive: deserializeMetricOccupancyLive,
  deserializeOccupancyPeriod: deserializeMetricOccupancyPeriod,
  deserializeAccesses: deserializeMetricAccesses,
  deserializeRevenues: deserializeMetricRevenues,

  // Lists
  granularities: metricGranularities,
  timeReferencesList: metricTimeReferencesList,
  parkingTypeList: metricParkingTypeList,

  // Utils
  defaultTimeReference: metricDefaultTimeReference,
  getPeriodDates: metricGetPeriodDates,

  // GraphQL
  queGetMetricsOccupancyRateLive,
  queGetMetricsOccupancyRatePeriod,
  queGetMetricsOccupancyRatePredicted,
  queGetActivityStatisticsOfTheDay,
  queGetMetricsAccesses,
  queGetMetricsRevenues,
  queGetMetricsRevenuesComparison,
  queGetMetricsRevenuesReport,
};

module.exports = metrics;



// WEBPACK FOOTER //
// ./models/cpm/metric.js